import React from "react";
import { createRoot } from "react-dom/client";
import { Provider as StoreProvider } from "react-redux";
import { RouterProvider } from "react-router-dom";
import { getEnv } from "@/env";
import { PersistGate } from "redux-persist/integration/react";

import oktaAuth from "@/okta-auth";
import { Security } from "@okta/okta-react";
import { toRelativeUrl } from "@okta/okta-auth-js";
import { Spinner } from "@8x8/oxygen-loaders";
import themes from "@8x8/oxygen-theme";
import { OxygenProvider } from "@8x8/oxygen-config";
import { router } from "@/react/routing/router";
import { persistor, store } from "@/react/store/store";
import { addEventListener, EVENT_TYPES, triggerEvent } from "@/events";
import configureOxygenThemes from "@/react/theme/configureOxygenThemes";
import { applyVueCustomer } from "@/react/features/customer";
import { setIsIframe } from "@/react/store/appSlice";
import { setCustomer } from "@/react/features/customer/customerSlice";
import { extractUserFromAuthState } from "@/react/features/auth/extractUserFromAuthState";
import { CustomerSelectModal } from "@/react/components/customer/modal";
import { setUser } from "@/react/store/userSlice";

import { O11yHub } from "@/react/observability";
import Debug from "@/react/debug";

addEventListener(EVENT_TYPES.SYNC_IS_IFRAME, (e) => {
  const { detail: isIframe } = e;
  store.dispatch(setIsIframe(isIframe));
});

addEventListener(EVENT_TYPES.SYNC_CUSTOMER, (e) => {
  const { detail: customer } = e;
  if (getEnv("VUE_APP_ENABLE_REACT_CUSTOMER_MODAL", true)) {
    store.dispatch(applyVueCustomer(customer));
  } else {
    store.dispatch(setCustomer(customer));
  }
});

// Needed for the Oxygen Components used in Vue
configureOxygenThemes(themes.novo);

// Synchronize React Router changes with Vue Router
router.subscribe((routerState) => {
  triggerEvent(
    EVENT_TYPES.SYNC_VUE_ROUTE,
    routerState.location.pathname +
      routerState.location.search +
      routerState.location.hash
  );
});

// Synchronize Redux with user from Okta AuthState
oktaAuth.authStateManager.subscribe((authState) => {
  const user = extractUserFromAuthState(authState);
  Debug.info(`Auth state changed, authenticated: ${authState.isAuthenticated}`);
  triggerEvent(EVENT_TYPES.SYNC_USER, user);
  store.dispatch(setUser(user));
});

const restoreOriginalUri = (_oktaAuth, originalUri) =>
  router.navigate(toRelativeUrl(originalUri || "/", window.location.origin));

const App = () => {
  return (
    <StoreProvider store={store}>
      <OxygenProvider themeName="light">
        <PersistGate loading={<Spinner />} persistor={persistor}>
          <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
            <CustomerSelectModal />
            <RouterProvider router={router} />
            {/* O11yHub does not render anything,
                it is here to handle state changes for observability on a general scale */}
            <O11yHub />
          </Security>
        </PersistGate>
      </OxygenProvider>
    </StoreProvider>
  );
};

export const renderReact = async (el) => {
  return createRoot(el).render(<App />);
};
